import { useEffect, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import type { CastPortrait } from './castPortraits'
import { useSocialPreview } from './useTikTokPreview'

type CastPortraitTileProps = {
  portrait: CastPortrait
  onOpen: () => void
}

export default function CastPortraitTile({ portrait, onOpen }: CastPortraitTileProps) {
  const tileRef = useRef<HTMLButtonElement>(null)
  const [nearViewport, setNearViewport] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)
  const preview = useSocialPreview(portrait.postUrl, portrait.platform, nearViewport)

  useEffect(() => {
    const tile = tileRef.current
    if (!tile || !('IntersectionObserver' in window)) {
      setNearViewport(true)
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      setNearViewport(true)
      observer.disconnect()
    }, { rootMargin: '240px' })

    observer.observe(tile)
    return () => observer.disconnect()
  }, [])

  const hasPhoto = preview.status === 'ready' && Boolean(preview.thumbnailUrl) && !imageFailed

  return (
    <button
      ref={tileRef}
      className={`cast-tile cast-tile--${portrait.tone} ${hasPhoto ? 'is-photo-ready' : ''}`}
      type="button"
      onClick={onOpen}
      aria-label={`Open ${portrait.name}'s profile and selected official portrait`}
    >
      <span className="cast-tile__art">
        {hasPhoto && <img className="cast-tile__photo" src={preview.thumbnailUrl} alt="" aria-hidden="true" loading="lazy" decoding="async" referrerPolicy="no-referrer" style={{ objectPosition: `center ${portrait.focusY ?? 20}%` }} onError={() => setImageFailed(true)} />}
        <span className="cast-tile__ring"><span>{portrait.initials}</span></span>
        <span className="cast-tile__source"><i /> {portrait.platform.toUpperCase()} POST</span>
      </span>
      <span className="cast-tile__copy">
        <strong>{portrait.shortName}</strong>
        <small>{portrait.showLabel}</small>
        <em>Open profile <ChevronRight size={12} /></em>
      </span>
    </button>
  )
}
